import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ComplianceBar } from "@/components/Compliance";
import { complianceColor } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2, ChevronRight } from "lucide-react";

const LEGEND = [
  { label: "90% o más", cls: "bg-emerald-500", t: "Verde" },
  { label: "80–89%", cls: "bg-blue-500", t: "Azul" },
  { label: "70–79%", cls: "bg-yellow-500", t: "Amarillo" },
  { label: "menos de 70%", cls: "bg-red-500", t: "Rojo" },
];

export default function Compliance() {
  const [period, setPeriod] = useState("mes");
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", period],
    queryFn: () => api.get(`/dashboard?period=${period}`).then((r) => r.data),
  });

  if (isLoading || !data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const { by_area, by_employee } = data;

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Cumplimiento</h1>
          <p className="text-sm text-slate-500">Semáforo de desempeño por área y funcionario</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 bg-white" data-testid="compliance-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hoy">Hoy</SelectItem>
            <SelectItem value="semana">Esta semana</SelectItem>
            <SelectItem value="mes">Este mes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Legend / semáforo */}
      <div className="flex flex-wrap gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        {LEGEND.map((l) => (
          <div key={l.t} className="flex items-center gap-2 text-xs">
            <span className={cn("h-3 w-3 rounded-full", l.cls)} />
            <span className="font-semibold text-slate-700">{l.t}</span>
            <span className="text-slate-400">{l.label}</span>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Por área</h2>
          <div className="space-y-4">
            {by_area.map((a) => (
              <div key={a.id}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">{a.name} <span className="text-slate-400">({a.total})</span></span>
                  <span className={cn("font-bold", complianceColor(a.compliance).text)}>{a.compliance}%</span>
                </div>
                <ComplianceBar pct={a.compliance} showLabel={false} height="h-3" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Por funcionario</h2>
          <div className="space-y-2">
            {by_employee.map((e) => {
              const c = complianceColor(e.compliance);
              return (
                <button key={e.id} onClick={() => navigate(`/tareas?assignee_id=${e.id}`)}
                  className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition hover:bg-slate-50" data-testid={`compliance-emp-${e.id}`}>
                  <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                    {e.avatar ? <img src={e.avatar} alt="" className="h-full w-full object-cover" /> : e.name[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="truncate text-sm font-semibold text-slate-800">{e.name}</span>
                      <span className={cn("text-sm font-bold", c.text)}>{e.compliance}%</span>
                    </div>
                    <ComplianceBar pct={e.compliance} showLabel={false} height="h-2" />
                    <div className="mt-1 flex gap-3 text-[11px] text-slate-400">
                      <span>{e.total} total</span>
                      <span className="text-emerald-600">{e.completada} completadas</span>
                      <span className="text-red-500">{e.vencida} vencidas</span>
                      <span className="text-yellow-600">{e.pendiente} pendientes</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
